import {
  AspectRatio,
  Calculate,
  Check,
  CloseFullscreen,
  Delete,
  Edit,
  PriorityHigh,
  Restore,
  Undo,
} from "@mui/icons-material";
import {
  Card,
  Checkbox,
  CircularProgress,
  Grid,
  Icon,
  IconButton,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { makeStyles } from "@mui/styles";
import ConfirmationModal from "components/ConfirmationModal";
import DataTableWithSubrows from "components/DataTableWithSubrows";
import CustomTextField from "components/Form/CustomTextField";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import PictureAvatar from "components/PictureAvatar";
import { defaultReportModalValues } from "components/ReportModal/fixtures";
import SplitPanelSelector from "components/SplitPanelSelector";
import { useAppContextController } from "context/AppContext";
import useHandleBackButtonSubGrid from "hooks/useHandleBackButtonSubGrid";
import useSessionAuth from "hooks/useSessionAuth";
import { cloneDeep, isArray } from "lodash";
import moment from "moment";
import { useSnackbar } from "notistack";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CommandManager } from "utils/classes/commandManager";
import { SaveTimecardDataCommand } from "utils/classes/saveTimecardDataCommand";
import { calculateTotalHours } from "utils/helpers/payrollHelper";
import { getCompanyImageUrl } from "utils/helpers/upload";
import useArrowKeysFieldSelection from "utils/useArrowKeysFieldSelection";
import useSort from "utils/useSort";
import { v4 as uuidv4 } from "uuid";
import createTimecardCorrection from "../../actions/createTimecardCorrection";
import exportPayrollBatch from "../../actions/exportPayrollBatch";
import fetchEventRosterForPayrollEditor from "../../actions/fetchEventRosterForPayrollEditor";
import fetchJobRosterForPayrollEditor from "../../actions/fetchJobRosterForPayrollEditor";
import getPayrollStatus from "../../actions/getPayrollStatus";
import resetPayrollBatch from "../../actions/resetPayrollBatch";
import savePayrollBatchRow from "../../actions/savePayrollBatchRow";
import submitPayrollBatch from "../../actions/submitPayrollBatch";
import AddDeductionsModal from "../AddDeductionsModal";
import EditableCellDropdown from "../EditableCellDropdown";
import EditableCellNumber from "../EditableCellNumber";
import TaxDetailsModal from "../TaxDetailsModal";
import TaxTotalsModal from "../TaxTotalsModal";
import TimecardEditorFilterCards from "../TimecardEditorFilterCards";
import { TimecardEditorPartnerMembersModal } from "../TimecardEditorTable/components/TimecardEditorPartnerMembersModal";
import PayrollEditorTableActions from "./components/PayrollEditorTableActions";
import updateInvoiceBatch from "../../actions/updateInvoiceBatch";
import updatePayrollBatch from "../../actions/updatePayrollBatch";

const useStyle = makeStyles({
  box: {
    marginTop: 20,
    overflow: "visible!important",
  },
  addButton: {
    fontSize: 40,
  },
});

const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 500,
    whiteSpace: "pre-line",
  },
});

const defaultMinimizedPageSize = 10;

const PayrollEditorTable = ({
  title = "Payroll Editor",
  pageSize = 5,
  setFilters,
  navigateToAction = "",
  parentPage,
  parentFilters,
  parentNavigateWithBack,
  currentPeriod,
  fullscreen,
  panelOptions,
  burdenRate,
  isLoadingBurdenRate,
}) => {
  const queryClient = useQueryClient();
  const { action, id, eventUrl, jobSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const setNavigateUrl = useNavigate();
  const [genericModalOpen, setGenericModalOpen] = useState(false);
  const classes = useStyle();
  const {
    currentApplicant,
    setCurrentApplicant,
    currentVenue,
    venues,
    userType,
    currentLoggedUser,
    currentEvent,
    currentJob,
    company,
  } = useAppContextController();

  const isShiftJob = !!jobSlug;

  const allSearchParams = useMemo(() => {
    const allParams = {};
    const paramsArray = [];
    searchParams.entries().forEach(([key, value]) => {
      // Exclude "panel" query param
      if (key === "panel") return;

      if (searchParams.getAll(key).length > 1) {
        allParams[key] = encodeURIComponent(searchParams.getAll(key));
      } else {
        allParams[key] = encodeURIComponent(value);
      }
      paramsArray.push(`${key}=${allParams[key]}`);
    });
    return paramsArray;
  }, [searchParams]);

  const queryParamsHaveChangedRef = useRef(true);
  const [expandedIndexes, setExpandedIndexes] = useState({}); // Index of the collapsible item currently expanded
  const [modalInfo, setModalInfo] = useState({ data: currentApplicant });
  const [isBulkMessageModalOpen, toggleBulkMessageModal] = useState(false);
  const [isStaffingPoolModalOpen, toggleStaffingPoolModalOpen] = useState(false);
  const [openReportMessageModal, setOpenReportMessageModal] = useState(false);
  const [tableQueryDetails, setTableQueryDetails] = useState(null);
  const [chartQueryDetails, setChartQueryDetails] = useState(null);
  const [tableReportData, setTableReportData] = useState(null);
  const [chartReportData, setChartReportData] = useState(null);
  const [reportModalValues, setReportModalValues] = useState(defaultReportModalValues);
  const [commandManager, setCommandManager] = useState(() => new CommandManager());
  const [isPartnerMembersModalOpen, setPartnerMembersModalOpen] = useState(false);
  const [isResetConfirmationModalOpen, setResetConfirmationModalOpen] = useState(false);
  const [isSubmitConfirmationModalOpen, setSubmitConfirmationModalOpen] = useState(false);
  const [isExportConfirmationModalOpen, setExportConfirmationModalOpen] = useState(false);
  const [selectedRowForDeductions, setSelectedRowForDeductions] = useState(null);
  const [selectedRowForTaxDetails, setSelectedRowForTaxDetails] = useState(null);
  const [selectedRowForCorrection, setSelectedRowForCorrection] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showRosterData, setShowRosterData] = useState(true);
  const [taxTotalsOpen, setTaxTotalsOpen] = useState(false);
  const [departmentName, setDepartmentName] = useState("");
  const { enqueueSnackbar } = useSnackbar();

  const d = new Date();
  d.setDate(d.getDate() + 2);
  const today = d.toISOString().split("T")[0];
  const eventDay = new Date(currentEvent?.eventDate).getTime() <= new Date(today).getTime();

  const { logoutAuth0User } = useSessionAuth();

  const [applicantsFilters, setApplicantsFilters] = useState({
    type: "",
  });

  const isPartners = applicantsFilters?.status === "Partner";

  const [timecardNotSubmitted, setTimecardNotSubmitted] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10); // Default pageSize higher than 10. Don't use preferred pageSize

  const navigate = useNavigate();

  const toggleFullscreen = (e) => {
    e.stopPropagation();
    setPage(1);
    navigate(
      `/payroll/${isShiftJob ? "job" : "event"}/${
        isShiftJob ? currentJob?.jobSlug : currentEvent?.eventUrl
      }/action/payroll${allSearchParams?.length ? `?${allSearchParams.join("&")}` : ""}`
    );
  };

  const minimize = (e) => {
    e.stopPropagation();
    setPage(1);
    navigate(
      `/payroll/${isShiftJob ? "job" : "event"}/${
        isShiftJob ? currentJob?.jobSlug : currentEvent?.eventUrl
      }/action/${isShiftJob ? "jobinfo" : "eventsinfo"}?panel=payroll${
        allSearchParams?.length ? `&${allSearchParams.join("&")}` : ""
      }`
    );
  };

  // Handle back button from parent screens
  const { navigateWithBack } = useHandleBackButtonSubGrid({
    parentPage,
    parentFilters,
    setPage,
    setFilters: setApplicantsFilters,
    page,
    filters: applicantsFilters,
    navigateFunc: setNavigateUrl,
    navigateWithBackFunc: parentNavigateWithBack,
  });

  const handlePanelChange = useCallback(
    (e, panel) => {
      e.stopPropagation();
      if (["timecard", "payroll", "invoice"].includes(panel)) {
        navigate(
          `/payroll/${isShiftJob ? "job" : "event"}/${
            isShiftJob ? currentJob?.jobSlug : currentEvent?.eventUrl
          }/action/${panel}${allSearchParams?.length ? `?${allSearchParams.join("&")}` : ""}`
        );
        return;
      }
      navigate(
        `/payroll/${isShiftJob ? "job" : "event"}/${
          isShiftJob ? currentJob?.jobSlug : currentEvent?.eventUrl
        }/action/${isShiftJob ? "jobinfo" : "eventsinfo"}?panel=${panel}${
          allSearchParams?.length ? `&${allSearchParams.join("&")}` : ""
        }`
      );
    },
    [allSearchParams, currentEvent?.eventUrl, currentJob?.jobSlug, isShiftJob, navigate]
  );

  const localPanelOptions = useMemo(() => {
    return panelOptions?.map((op) => ({
      ...op,
      hasShadow: op.name === "status" ? "emd" : "",
      fn: (e) => (op.name === "status" ? () => {} : handlePanelChange(e, op.name)),
    }));
  }, [handlePanelChange, panelOptions]);

  const { order, orderBy, toggleSort } = useSort("lastName", "asc");
  const options = useMemo(
    () => ({ page, limit: fullscreen ? limit : defaultMinimizedPageSize, order, orderBy }),
    [fullscreen, limit, order, orderBy, page]
  );
  const allOptions = useMemo(
    () => ({
      ...options,
      ...(isShiftJob
        ? {
            jobSlug: currentJob?.jobSlug,
            ...(currentPeriod?.length === 2
              ? {
                  startDate: currentPeriod[0].toISOString(),
                  endDate: currentPeriod[1].toISOString(),
                }
              : {}),
          }
        : {
            eventUrl: currentEvent?.eventUrl,
            filters: {
              ...applicantsFilters,
            },
            ...(currentPeriod?.length === 2
              ? {
                  startDate: currentPeriod[0].toISOString(),
                  endDate: currentPeriod[1].toISOString(),
                }
              : {}),
          }),
    }),
    [
      applicantsFilters,
      currentEvent?.eventUrl,
      currentJob?.jobSlug,
      currentPeriod,
      isShiftJob,
      options,
    ]
  );

  const allOptionsPayrollStatus = useMemo(() => {
    return {
      ...(isShiftJob
        ? {
            jobSlug: currentJob?.jobSlug,
            ...(currentPeriod?.length === 2
              ? {
                  startDate: currentPeriod[0].toISOString(),
                  endDate: currentPeriod[1].toISOString(),
                }
              : {}),
          }
        : {
            eventUrl: currentEvent?.eventUrl,
            ...(currentPeriod?.length === 2
              ? {
                  startDate: currentPeriod[0].toISOString(),
                  endDate: currentPeriod[1].toISOString(),
                }
              : {}),
          }),
    };
  }, [currentEvent?.eventUrl, currentJob?.jobSlug, currentPeriod, isShiftJob]);

  useEffect(() => {
    return () => {
      // Clear all cached data
      queryClient.removeQueries({ queryKey: ["eventRosterPayrollList", allOptions] });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetExpandedIndexes = useCallback((data) => {
    const newExpandedIndexes = {};

    if (!data?.length) return;

    for (let index = 0; index < data.length; index += 1) {
      const element = data[index];
      if (element?.subRows?.length) {
        newExpandedIndexes[index] = true;
      }
    }

    setExpandedIndexes(newExpandedIndexes);
  }, []);

  const {
    data: rosterList,
    isLoading,
    refetch: refetchRosterList,
  } = useQuery(
    ["eventRosterPayrollList", allOptions],
    () =>
      isShiftJob
        ? fetchJobRosterForPayrollEditor(allOptions)
        : fetchEventRosterForPayrollEditor(allOptions),
    {
      enabled: isShiftJob ? !!currentJob?.jobSlug : !!currentEvent?.eventUrl,
      onSuccess: (res) => {
        if (queryParamsHaveChangedRef.current) {
          resetExpandedIndexes(res?.data?.data);
        }
        queryParamsHaveChangedRef.current = false;
        if (res?.code === "TIMECARD_NOT_SUBMITTED") {
          setTimecardNotSubmitted(true);
        } else {
          setTimecardNotSubmitted(false);
        }
      },
      select: (data) => {
        const newData = data?.data?.data?.map((item) => ({
          ...item,
          uniqueKey: uuidv4(),
          ...(item?.subRows?.length
            ? {
                subRows: item.subRows.map((subRow) => ({
                  ...subRow,
                  uniqueKey: uuidv4(),
                  ...(subRow?.subRowsCritical?.length
                    ? {
                        subRowsCritical: subRow.subRowsCritical.map((subRowCritical) => ({
                          ...subRowCritical,
                          uniqueKey: uuidv4(),
                        })),
                      }
                    : {}),
                })),
              }
            : {}),
          ...(item?.subRowsCritical?.length
            ? {
                subRowsCritical: item.subRowsCritical.map((subRowCritical) => ({
                  ...subRowCritical,
                  uniqueKey: uuidv4(),
                })),
              }
            : {}),
          ...(item?.members?.length
            ? {
                members: item.members.map((mem) => ({
                  ...mem,
                  uniqueKey: uuidv4(),
                })),
              }
            : {}),
        }));
        return {
          ...data,
          data: {
            ...data?.data,
            data: newData,
          },
        };
      },
    }
  );

  const {
    data: payrollStatus,
    isLoading: isLoadingPayrollStatus,
    refetch: refetchPayrollStatus,
  } = useQuery(
    ["payrollStatus", allOptionsPayrollStatus],
    () => getPayrollStatus(allOptionsPayrollStatus),
    {}
  );

  const payrollSubmitted = payrollStatus?.data?.payrollStatus === "Submitted";
  const correctionActive = payrollStatus?.data?.correctionActive;

  const eventIsInThePast =
    !isShiftJob &&
    currentEvent?.eventDate &&
    new Date(currentEvent.eventDate).toISOString() < new Date().toISOString();
  const wrongTimePeriod =
    !isShiftJob &&
    currentEvent?.eventDate &&
    (new Date(currentEvent.eventDate).toISOString() < currentPeriod?.[0]?.toISOString() ||
      new Date(currentEvent.eventDate).toISOString() > currentPeriod?.[1]?.toISOString());

  const stringifiedOptions = JSON.stringify(allOptions);
  useEffect(() => {
    setCommandManager(() => new CommandManager());
    resetExpandedIndexes(rosterList?.data?.data);
    queryParamsHaveChangedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringifiedOptions]);

  const resetDataMutation = useMutation(resetPayrollBatch);
  const saveRowMutation = useMutation(savePayrollBatchRow);
  const exportPayrollMutation = useMutation(exportPayrollBatch);
  const createCorrectionMutation = useMutation(createTimecardCorrection);
  const updatePayrollBatchMutation = useMutation(updatePayrollBatch);
  const submitDataMutation = useMutation(submitPayrollBatch, {
    onError: (e) => {
      const errorMessage =
        e?.response?.data?.message ?? "An error has occurred submitting the Payroll";
      enqueueSnackbar(errorMessage, { variant: "error" });
    },
  });

  // --------- ROSTER ---------
  const rosterApplicants = useMemo(() => {
    if (!rosterList?.data?.data?.length) return [];
    return rosterList.data.data.map((rItem) => rItem.rosterData);
  }, [rosterList?.data?.data]);
  // --------------------------

  useEffect(() => {
    if (rosterList?.data?.length && rosterList.data.length === 1) {
      // setSelectedId(applicants.data[0]._id);
      setCurrentApplicant(rosterList.data[0]);
    }
  }, [rosterList?.data]);

  useEffect(() => {
    if (action && rosterList?.data?.length) {
      const [setApplicantAction] = rosterList?.data?.filter((item) => item?._id === id);
      setCurrentApplicant(setApplicantAction);
    }
  }, [action, id, rosterList?.data]);

  const autosaveQueueRef = useRef([]);
  const isProcessingQueueRef = useRef(false);

  // ------------ FIELD SWITCH ------------
  const inputRefs = useRef({});
  const getFinalFieldRowIndex = useCallback(
    (i, iSub) => {
      // If there are shift job items with subitems, we need to skip that index, as it doesn't have fields
      // Also, if there's an expanded shift job item, we need to increase the index by the number of items

      if (!rosterList?.data?.data?.length) return 0;

      // Check how many rows with subrows exist before the current index
      const newIndex = rosterList.data.data.reduce((prev, current, ind) => {
        if (ind > i) return prev;
        if (current?.subRows?.length) {
          // This row has subrows
          if (!expandedIndexes[ind]) {
            // If it's closed, skip it
            return prev;
          }
          if (expandedIndexes[i] && iSub != null) {
            // If it's open, and it's the current index, calculate the subrow index
            return prev + iSub;
          }
          // If it's open, but it's not the current index, increment the sum by the number of subitems
          return prev + current?.subRows?.length;
        }

        if (ind === i) return prev;
        return prev + 1;
      }, 0);

      return newIndex;
    },
    [expandedIndexes, rosterList?.data?.data]
  );
  const gridDimensions = useMemo(
    () => ({
      rows: getFinalFieldRowIndex(rosterList?.data?.data?.length ?? 0),
      cols: 3,
    }),
    [getFinalFieldRowIndex, rosterList?.data?.data?.length]
  );
  const { getFieldKey, handleKeyDown: handleKeyDownField } = useArrowKeysFieldSelection({
    inputRefs,
    gridDimensions,
  });
  // --------------------------------------

  const handleSelectPartnerMembers = (row) => {
    setSelectedPartner(row);
    setPartnerMembersModalOpen(true);
  };

  const handleClosePartnerMembersModal = () => {
    setSelectedPartner(null);
    setPartnerMembersModalOpen(false);
  };

  const handleToggleMinimizedInformation = () => {
    setShowRosterData((current) => !current);
  };

  // Update local cache of query data
  const updateDataCache = (applicantId, newData, slug) => {
    const currentCacheData = queryClient.getQueryData(["eventRosterPayrollList", allOptions]);
    const dataCopy = cloneDeep(currentCacheData);
    const dataArray = dataCopy?.data?.data?.map((row) => {
      if (
        !row?.subRows?.length &&
        ((!!applicantId && row?._id === applicantId) || (!!slug && row?.slug === slug))
      ) {
        const result = {
          ...row,
          rosterData: {
            ...row?.rosterData,
            ...newData,
            timeIn: row?.rosterData?.timeIn,
            timeOut: row?.rosterData?.timeOut,
          },
        };
        if (row?.subRowsCritical?.length) {
          // This item has critical subrows (overtime hours)
          // Update their payRate/billRate if necessary
          result.subRowsCritical = result.subRowsCritical.map((src) => {
            const newPayRate = Math.round(parseFloat(newData.payRate?.toString()) * 100) / 100;
            const newBillRate = Math.round(parseFloat(newData.billRate?.toString()) * 100) / 100;
            return {
              ...src,
              rosterData: {
                ...src?.rosterData,
                payRate: newPayRate * 1.5,
                billRate: newBillRate * 1.5,
                timeIn: src?.rosterData?.timeIn,
                timeOut: src?.rosterData?.timeOut,
              },
            };
          });
        }

        return result;
      }
      if (!!row?.subRows?.length && newData.punchId) {
        // We couldn't find a matching row in the main list, but the data includes a punchId
        // So this could be one of the subRows of a Shift Job row
        return {
          ...row,
          subRows: row.subRows.map((sr) => {
            if (sr.punchId === newData.punchId) {
              const result = {
                ...sr,
                rosterData: {
                  ...sr?.rosterData,
                  ...newData,
                  timeIn: sr?.rosterData?.timeIn,
                  timeOut: sr?.rosterData?.timeOut,
                },
              };
              if (sr?.subRowsCritical?.length) {
                // This item has critical subrows (overtime hours)
                // Update their payRate/billRate if necessary
                result.subRowsCritical = result.subRowsCritical.map((src) => {
                  const newPayRate =
                    Math.round(parseFloat(newData.payRate?.toString()) * 100) / 100;
                  const newBillRate =
                    Math.round(parseFloat(newData.billRate?.toString()) * 100) / 100;
                  return {
                    ...src,
                    rosterData: {
                      ...src?.rosterData,
                      payRate: newPayRate * 1.5,
                      billRate: newBillRate * 1.5,
                      timeIn: src?.rosterData?.timeIn,
                      timeOut: src?.rosterData?.timeOut,
                    },
                  };
                });
              }
              return result;
            }
            return sr;
          }),
        };
      }
      return row;
    });
    if (dataCopy?.data?.data) dataCopy.data.data = dataArray;
    queryClient.setQueryData(["eventRosterPayrollList", allOptions], dataCopy);
  };

  const pushChange = async (requestData, originalData) => {
    const command = new SaveTimecardDataCommand(
      autosaveQueueRef,
      isProcessingQueueRef,
      requestData,
      originalData,
      updateDataCache,
      saveRowMutation,
      true
    );
    commandManager.executeCommand(command);
  };

  // Undo function
  const handleUndo = useCallback(() => {
    if (commandManager.undo()) enqueueSnackbar("Last change undone", { variant: "success" });
    else enqueueSnackbar("Nothing to undo", { variant: "error" });
  }, [commandManager, enqueueSnackbar]);

  // Reset data function
  const handleReset = useCallback(async () => {
    const result = await resetDataMutation.mutateAsync(
      isShiftJob && currentJob?.jobSlug
        ? {
            jobSlug: currentJob?.jobSlug,
            data: {
              startDate: currentPeriod?.[0],
              endDate: currentPeriod?.[1],
            },
          }
        : {
            eventUrl: currentEvent?.eventUrl,
            data: {
              startDate: currentPeriod?.[0],
              endDate: currentPeriod?.[1],
            },
          }
    );
    if (result?.data?.success) {
      enqueueSnackbar("Payroll reset successfully", { variant: "success" });
      setCommandManager(() => new CommandManager());
      resetExpandedIndexes(rosterList?.data?.data);
      queryClient.removeQueries({ queryKey: ["eventRosterPayrollList", allOptions] });
      refetchRosterList();
      refetchPayrollStatus();
    } else {
      enqueueSnackbar("An error has occurred", { variant: "success" });
    }
  }, [
    allOptions,
    currentEvent?.eventUrl,
    currentJob?.jobSlug,
    currentPeriod,
    enqueueSnackbar,
    isShiftJob,
    queryClient,
    refetchPayrollStatus,
    refetchRosterList,
    resetDataMutation,
    resetExpandedIndexes,
    rosterList?.data?.data,
  ]);

  const handleConfirmCreateCorrection = useCallback(
    async (row) => {
      const result = await createCorrectionMutation.mutateAsync(
        isShiftJob && currentJob?.jobSlug
          ? {
              jobSlug: currentJob?.jobSlug,
              data: {
                applicantId: row?._id,
                timeIn: row?.rosterData?.timeIn,
                timeOut: row?.rosterData?.timeOut,
                payRate: row?.rosterData?.payRate,
                billRate: row?.rosterData?.billRate,
                deductionCode: row?.rosterData?.deductionCode,
                deductionAmount: row?.rosterData?.deductionAmount,
                deductionAmountType: row?.rosterData?.deductionAmountType,
                // deductionMaxAmount: newData.deductionMaxAmount,
                // deductionMaxAmountType: newData.deductionMaxAmountType,
                deductionSponsor: row?.rosterData?.deductionSponsor,
                startDate: currentPeriod?.[0],
                endDate: currentPeriod?.[1],
                timecardId: row?.punchId,
                salary: row?.rosterData?.salary,
                earningId: row?.rosterData?.earningId,
              },
            }
          : {
              eventUrl: currentEvent?.eventUrl,
              data: {
                applicantId: row?._id,
                timeIn: row?.rosterData?.timeIn,
                timeOut: row?.rosterData?.timeOut,
                payRate: row?.rosterData?.payRate,
                billRate: row?.rosterData?.billRate,
                deductionCode: row?.rosterData?.deductionCode,
                deductionAmount: row?.rosterData?.deductionAmount,
                deductionAmountType: row?.rosterData?.deductionAmountType,
                // deductionMaxAmount: newData.deductionMaxAmount,
                // deductionMaxAmountType: newData.deductionMaxAmountType,
                deductionSponsor: row?.rosterData?.deductionSponsor,
                startDate: currentPeriod?.[0],
                endDate: currentPeriod?.[1],
                salary: row?.rosterData?.salary,
                earningId: row?.rosterData?.earningId,
              },
            }
      );
      if (result?.data?.success) {
        enqueueSnackbar("Correction created successfully", { variant: "success" });
        setCommandManager(() => new CommandManager());
        queryClient.removeQueries({ queryKey: ["eventRosterList", allOptions] });
        refetchRosterList();
        refetchPayrollStatus();
      } else {
        enqueueSnackbar("An error has occurred", { variant: "success" });
      }
    },
    [
      allOptions,
      createCorrectionMutation,
      currentEvent?.eventUrl,
      currentJob?.jobSlug,
      currentPeriod,
      enqueueSnackbar,
      isShiftJob,
      queryClient,
      refetchPayrollStatus,
      refetchRosterList,
    ]
  );

  // Submit data function
  const handleSubmit = useCallback(async () => {
    const result = await submitDataMutation.mutateAsync(
      isShiftJob && currentJob?.jobSlug
        ? {
            jobSlug: currentJob?.jobSlug,
            data: {
              startDate: currentPeriod?.[0],
              endDate: currentPeriod?.[1],
            },
          }
        : {
            eventUrl: currentEvent?.eventUrl,
            data: {
              startDate: currentPeriod?.[0],
              endDate: currentPeriod?.[1],
            },
          }
    );
    if (result?.data?.success) {
      enqueueSnackbar("Payroll submitted successfully", { variant: "success" });
      setCommandManager(() => new CommandManager());
      queryClient.removeQueries({ queryKey: ["eventRosterPayrollList", allOptions] });
      setExportConfirmationModalOpen(true);
      refetchRosterList();
      refetchPayrollStatus();
    } else {
      enqueueSnackbar("An error has occurred", { variant: "success" });
    }
  }, [
    allOptions,
    currentEvent?.eventUrl,
    currentJob?.jobSlug,
    currentPeriod,
    enqueueSnackbar,
    isShiftJob,
    queryClient,
    refetchPayrollStatus,
    refetchRosterList,
    submitDataMutation,
  ]);

  // Submit data function
  const handleExport = useCallback(async () => {
    const result = await exportPayrollMutation.mutateAsync(
      isShiftJob && currentJob?.jobSlug
        ? {
            data: {
              startDate: currentPeriod?.[0],
              endDate: currentPeriod?.[1],
              jobSlugs: [currentJob?.jobSlug],
            },
          }
        : {
            data: {
              startDate: currentPeriod?.[0],
              endDate: currentPeriod?.[1],
              eventUrls: [currentEvent?.eventUrl],
            },
          }
    );
    if (result?.data?.success) {
      window.open(`${getCompanyImageUrl(company)}${result.data.exportUrl}`, "_blank");
      enqueueSnackbar("Payroll exported successfully", { variant: "success" });
    } else {
      enqueueSnackbar("An error has occurred", { variant: "success" });
    }
  }, [
    company,
    currentEvent?.eventUrl,
    currentJob?.jobSlug,
    currentPeriod,
    enqueueSnackbar,
    exportPayrollMutation,
    isShiftJob,
  ]);

  // Toggle Shift Job sublist of rows
  const handleToggleSubList = useCallback(
    (index) => {
      if (expandedIndexes[index]) {
        setExpandedIndexes({ ...expandedIndexes, [index]: false });
      } else {
        setExpandedIndexes({ ...expandedIndexes, [index]: true });
      }
    },
    [expandedIndexes]
  );

  const onChangeField = (row, fieldName, fieldValue) => {
    const isForPartner = row?.slug;

    // Find if there's an edit for the current field
    const date = isShiftJob
      ? moment(row?.timeIn).format("MM/DD/YY")
      : moment(currentEvent?.eventDate).format("MM/DD/YY");
    const editKey = `${row?._id}-${date}-${row?.punchId ?? ""}`;

    const originalData = {
      timeIn: row?.rosterData?.timeIn,
      timeOut: row?.rosterData?.timeOut,
      payRate: row?.rosterData?.payRate,
      billRate: row?.rosterData?.billRate,
      deductionCode: row?.rosterData?.deductionCode,
      deductionAmount: row?.rosterData?.deductionAmount,
      deductionAmountType: row?.rosterData?.deductionAmountType,
      // deductionMaxAmount: row?.rosterData?.deductionMaxAmount,
      // deductionMaxAmountType: row?.rosterData?.deductionMaxAmountType,
      deductionSponsor: row?.rosterData?.deductionSponsor,
      salary: row?.rosterData?.salary ?? "No",
      earningId: row?.rosterData?.earningId,
    };

    const newData = { ...originalData };
    if (isArray(fieldName)) {
      let hasChanges = false;
      for (let index = 0; index < fieldName.length; index += 1) {
        const fieldN = fieldName[index];
        const fieldV = fieldValue[index];

        newData[fieldN] = fieldV;

        // eslint-disable-next-line eqeqeq
        if ((fieldV ?? "") != (originalData[fieldN] ?? "")) hasChanges = true;
      }
      if (!hasChanges) return;
    } else {
      newData[fieldName] = fieldValue;

      // eslint-disable-next-line eqeqeq
      if ((fieldValue ?? "") == (originalData[fieldName] ?? "")) return;
    }

    if (isShiftJob && currentJob?.jobSlug)
      pushChange(
        {
          jobSlug: currentJob?.jobSlug,
          data: {
            ...(isForPartner ? { partnerSlug: row?.slug } : { applicantId: row?._id }),
            timeIn: newData.timeIn,
            timeOut: newData.timeOut,
            payRate: newData.payRate,
            billRate: newData.billRate,
            deductionCode: newData.deductionCode,
            deductionAmount: newData.deductionAmount,
            deductionAmountType: newData.deductionAmountType,
            // deductionMaxAmount: newData.deductionMaxAmount,
            // deductionMaxAmountType: newData.deductionMaxAmountType,
            deductionSponsor: newData.deductionSponsor,
            salary: newData.salary,
            earningId: newData.earningId,
            startDate: currentPeriod?.[0],
            endDate: currentPeriod?.[1],
            timecardId: row?.punchId,
          },
        },
        originalData
      );
    else if (currentEvent?.eventUrl)
      pushChange(
        {
          eventUrl: currentEvent?.eventUrl,
          data: {
            ...(isForPartner ? { partnerSlug: row?.slug } : { applicantId: row?._id }),
            timeIn: newData.timeIn,
            timeOut: newData.timeOut,
            payRate: newData.payRate,
            billRate: newData.billRate,
            deductionCode: newData.deductionCode,
            deductionAmount: newData.deductionAmount,
            deductionAmountType: newData.deductionAmountType,
            // deductionMaxAmount: newData.deductionMaxAmount,
            // deductionMaxAmountType: newData.deductionMaxAmountType,
            deductionSponsor: newData.deductionSponsor,
            salary: newData.salary,
            earningId: newData.earningId,
            startDate: currentPeriod?.[0],
            endDate: currentPeriod?.[1],
          },
        },
        originalData
      );
  };

  const handleAddDeduction = (row, deduction) => {
    onChangeField(
      row,
      [
        "deductionCode",
        "deductionAmount",
        "deductionAmountType",
        // "deductionMaxAmount",
        // "deductionMaxAmountType",
        "deductionSponsor",
      ],
      [
        deduction?.code,
        deduction?.amount,
        deduction?.amountType,
        // deduction?.maxAmount,
        // deduction?.maxAmountType,
        deduction?.sponsor,
      ]
    );
  };

  const handleUpdateDepartmentName = () => {
    if (!payrollStatus?.data?._id) return;

    updatePayrollBatchMutation.mutateAsync({
      batchId: payrollStatus?.data?._id,
      data: { departmentName: departmentName ?? "" },
    });
  };

  useEffect(() => {
    setDepartmentName(payrollStatus?.data?.departmentName);
  }, [payrollStatus?.data?.departmentName]);

  // Keyboard shortcut for UNDO
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check if any input field is focused
      // eslint-disable-next-line prefer-destructuring
      const activeElement = document.activeElement;
      const isInputFocused =
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.contentEditable === "true" ||
          activeElement.isContentEditable);

      // Only handle shortcuts when no input is focused
      if (!isInputFocused && (event.ctrlKey || event.metaKey)) {
        if (event.key === "z" && !event.shiftKey && !isPartnerMembersModalOpen) {
          event.preventDefault();
          handleUndo();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleUndo, isPartnerMembersModalOpen]);

  const validatePayBillRate = (newVal, compareVal, field) => {
    let result;
    if (!newVal) return true;
    if (field === "billRate") {
      result =
        compareVal == null ||
        compareVal === "" ||
        parseFloat(newVal.toString()) >= parseFloat(compareVal.toString());
    } else {
      result =
        compareVal == null ||
        compareVal === "" ||
        parseFloat(newVal.toString()) <= parseFloat(compareVal.toString());
    }
    if (!result) enqueueSnackbar("Bill rate must be larger than Pay Rate", { variant: "error" });
    return result;
  };

  const calculatedBurdenRate = useMemo(() => {
    const burdenInfo = burdenRate?.data?.burdenInfo;
    const result =
      (burdenInfo?.payrollTaxes ?? 0) +
      (burdenInfo?.futa ?? 0) +
      (burdenInfo?.suta ?? 0) +
      (burdenInfo?.workersCompensation ?? 0) +
      (burdenInfo?.benefits ?? 0) +
      (burdenInfo?.administrativeCosts ?? 0);
    return result;
  }, [burdenRate?.data?.burdenInfo]);

  const extraColumns = useMemo(() => {
    const result = {};
    const data = rosterList?.data?.data ?? [];

    const processRow = (row) => {
      // Hours
      let hourDifference = 0;
      let hours = calculateTotalHours(row, currentEvent?.timeZone);
      if (correctionActive && row?.rosterData?.isCorrection) {
        // This row is corrected. Display difference of hours
        const originalHours = calculateTotalHours(
          {
            ...row,
            rosterData: {
              ...row?.rosterData,
              timeIn: row?.rosterData?.originalTimeIn,
              timeOut: row?.rosterData?.originalTimeOut,
            },
          },
          currentEvent?.timeZone
        );
        hourDifference = hours - originalHours;
        hourDifference = Math.round(hourDifference * 100) / 100;
        hours = hourDifference;
      }

      // TOTAL PAY
      const payRate = row?.rosterData?.payRate ? parseFloat(row?.rosterData?.payRate) : 0;
      let totalPay = payRate * hours;

      // SALARY
      if (row?.rosterData?.salary === "Yes") {
        totalPay = payRate;
      }

      // DEDUCTIONS
      if (row?.rosterData?.deductionAmount) {
        const deductionAmount = parseFloat(row?.rosterData?.deductionAmount?.toString());
        let finalDeduction = deductionAmount;
        if (row?.rosterData?.deductionAmountType === "percentage") {
          finalDeduction = totalPay * (deductionAmount / 100);
        }
        totalPay += finalDeduction;
      }

      // TOTAL BILL
      const billRate = row?.rosterData?.billRate ? parseFloat(row?.rosterData?.billRate) : 0;
      let totalBill = billRate * hours;

      // SALARY
      if (row?.rosterData?.salary === "Yes") {
        totalBill = billRate;
      }

      // BURDEN
      let burden = calculatedBurdenRate * totalPay;

      // GROSS MARGIN
      let grossMargin = totalBill - burden - totalPay;

      // If this is a group of rows, add the values of each subrow, including OT rows
      if (row?.subRows?.length) {
        totalPay = row.subRows.reduce((prev, current) => {
          let total = prev + (result[current?.uniqueKey]?.totalPay ?? 0);
          if (current?.subRowsCritical?.length) {
            total = current.subRowsCritical.reduce((prev2, current2) => {
              return prev2 + (result[current2?.uniqueKey]?.totalPay ?? 0);
            }, total);
          }
          return total;
        }, 0);

        totalBill = row.subRows.reduce((prev, current, ind, array) => {
          let total = prev + (result[current?.uniqueKey]?.totalBill ?? 0);
          if (current?.subRowsCritical?.length) {
            total = current.subRowsCritical.reduce((prev2, current2) => {
              return prev2 + (result[current2?.uniqueKey]?.totalBill ?? 0);
            }, total);
          }
          return total;
        }, 0);

        burden = row.subRows.reduce((prev, current, ind, array) => {
          let total = prev + (result[current?.uniqueKey]?.burden ?? 0);
          if (current?.subRowsCritical?.length) {
            total = current.subRowsCritical.reduce((prev2, current2) => {
              return prev2 + (result[current2?.uniqueKey]?.burden ?? 0);
            }, total);
          }
          return total;
        }, 0);

        grossMargin = row.subRows.reduce((prev, current, ind, array) => {
          let total = prev + (result[current?.uniqueKey]?.grossMargin ?? 0);
          if (current?.subRowsCritical?.length) {
            total = current.subRowsCritical.reduce((prev2, current2) => {
              return prev2 + (result[current2?.uniqueKey]?.grossMargin ?? 0);
            }, total);
          }
          return total;
        }, 0);
      }

      // If this is a partner, add the values of each member
      if (row?.members?.length) {
        totalPay = row.members.reduce((prev, current) => {
          const total = prev + (result[current?.uniqueKey]?.totalPay ?? 0);
          return total;
        }, 0);

        totalBill = row.members.reduce((prev, current, ind, array) => {
          const total = prev + (result[current?.uniqueKey]?.totalBill ?? 0);
          return total;
        }, 0);

        burden = row.members.reduce((prev, current, ind, array) => {
          const total = prev + (result[current?.uniqueKey]?.burden ?? 0);
          return total;
        }, 0);

        grossMargin = row.members.reduce((prev, current, ind, array) => {
          const total = prev + (result[current?.uniqueKey]?.grossMargin ?? 0);
          return total;
        }, 0);
      }

      totalPay = Math.round(totalPay * 100) / 100;
      totalBill = Math.round(totalBill * 100) / 100;
      burden = Math.round(burden * 100) / 100;
      grossMargin = Math.round(grossMargin * 100) / 100;

      let grossMarginPercentage = grossMargin / totalBill;
      grossMarginPercentage = Math.round(grossMarginPercentage * 100) / 100;

      return {
        totalPay,
        totalBill,
        burden,
        grossMargin,
        grossMarginPercentage,
      };
    };
    data.forEach((row) => {
      (row?.subRowsCritical ?? []).forEach((subRowCritical) => {
        result[subRowCritical?.uniqueKey] = processRow(subRowCritical);
      });
      (row?.subRows ?? []).forEach((subRow) => {
        result[subRow?.uniqueKey] = processRow(subRow);
        (subRow?.subRowsCritical ?? []).forEach((subRowSubCritical) => {
          result[subRowSubCritical?.uniqueKey] = processRow(subRowSubCritical);
        });
      });
      (row?.members ?? []).forEach((mem) => {
        result[mem?.uniqueKey] = processRow(mem);
      });
      result[row?.uniqueKey] = processRow(row);
    });
    return result;
  }, [calculatedBurdenRate, correctionActive, currentEvent?.timeZone, rosterList?.data?.data]);

  const columns = [
    ...(fullscreen
      ? [
          {
            title: "Avatar",
            field: "profileImg",
            width: 100,
            sortable: false,
            skipForSubtable: true,
            customCell: (image, field, row) => (
              <PictureAvatar
                image={row?.profileImg}
                firstName={row?.firstName}
                lastName={row?.lastName}
                userId={row?.userId ?? row.userRecordId ?? row?._id}
                size="md"
              />
            ),
          },
          {
            title: "Employee ID",
            field: "employeeID",
            skipForSubtable: true,
          },
        ]
      : []),
    {
      title: "Last Name",
      field: "lastName",
      skipForSubtable: true,
      customCell: (name, field, row) =>
        row ? (
          <CustomWidthTooltip
            title={`${row?.email || ""} \n ${row?.city || ""} ${row?.state || ""}`}
            sx={{ color: "red" }}
          >
            <div>{name}</div>
          </CustomWidthTooltip>
        ) : null,
    },
    {
      title: "First Name",
      field: "firstName",
      skipForSubtable: true,
      customCell: (name, field, row) =>
        row ? (
          <CustomWidthTooltip
            title={`${row?.email || ""} \n ${row?.city || ""} ${row?.state || ""}`}
            sx={{ color: "red" }}
          >
            <div>{name}</div>
          </CustomWidthTooltip>
        ) : null,
    },
    ...(fullscreen || !showRosterData
      ? [
          ...(isShiftJob
            ? [
                {
                  title: "Shift Name",
                  field: "shiftSlug",
                  customCell: (shiftSlug, field, row) => {
                    const jobShifts = currentJob?.shifts ?? [];
                    const matchingShift = jobShifts.find((s) => s.slug === shiftSlug);

                    if (matchingShift) return matchingShift.shiftName;
                    return shiftSlug;
                  },
                },
              ]
            : [
                {
                  title: "Type",
                  field: "status",
                  skipForSubtable: true,
                },
              ]),
          {
            title: "Position",
            field: "primaryPosition",
            sortable: false,
            customCell: (position, field, row) => {
              if (isShiftJob) return currentJob?.title;
              const DEFAULT_POSITION = "Event Staff";

              return row?.rosterData?.primaryPosition || DEFAULT_POSITION;
            },
          },
          {
            title: "Work Date",
            field: "rosterData",
            sortable: false,
            customCell: (rosterData, field, row) =>
              isShiftJob
                ? moment(row?.timeIn).format("MM/DD/YY")
                : moment(currentEvent?.eventDate).format("MM/DD/YY"),
          },
        ]
      : []),
    ...(fullscreen || showRosterData
      ? [
          {
            title: "Salary",
            field: "rosterData",
            width: 80,
            sortable: false,
            customCell: (rosterData, field, row, i, iSub, isCriticalSubRow) => {
              if (row?.slug || row?.subRows?.length) {
                return <MDTypography sx={{ textAlign: "left", pl: 1 }}>-</MDTypography>;
              }

              // Critical subrows => always displayed on screen in red (overtime rows)
              if (row?.subRows?.length) {
                return <MDTypography sx={{ textAlign: "left", pl: 1 }}>-</MDTypography>;
              }

              if (
                payrollSubmitted ||
                isCriticalSubRow ||
                (correctionActive && !row?.rosterData?.isCorrection)
              ) {
                return rosterData?.salary === "Yes" ? <Check /> : null;
              }

              return (
                <Checkbox
                  checked={rosterData?.salary === "Yes"}
                  onChange={(v) => onChangeField(row, "salary", v.target.checked ? "Yes" : "No")}
                />
              );
            },
          },
          {
            title: "Pay Rate",
            field: "rosterData",
            width: 130,
            sortable: false,
            customCell: (rosterData, field, row, i, iSub, isCriticalSubRow, absoluteIndex) => {
              const finalIndex = absoluteIndex;

              if (row?.slug || row?.subRows?.length) {
                return <MDTypography sx={{ textAlign: "left", pl: 1 }}>-</MDTypography>;
              }

              // Critical subrows => always displayed on screen in red (overtime rows)
              if (
                payrollSubmitted ||
                isCriticalSubRow ||
                (correctionActive && !row?.rosterData?.isCorrection)
              ) {
                return <span>{`$${row?.rosterData?.payRate || "0"}`}</span>;
              }

              return (
                <EditableCellNumber
                  amount={row?.rosterData?.payRate ?? ""}
                  setAmount={(val) => onChangeField(row, "payRate", val ? parseFloat(val) : val)}
                  // onValidate={(val) =>
                  //   validatePayBillRate(val, row?.rosterData?.billRate ?? "", "payRate")
                  // }
                  adornmentSymbol="$"
                  onKeyDown={(e) => handleKeyDownField(e, finalIndex, 0)}
                  ref={(el) => {
                    if (el) {
                      inputRefs.current[getFieldKey(finalIndex, 0)] = el;
                    }
                  }}
                />
              );
            },
          },
          {
            title: "Bill Rate",
            field: "rosterData",
            width: 130,
            sortable: false,
            customCell: (rosterData, field, row, i, iSub, isCriticalSubRow, absoluteIndex) => {
              const finalIndex = absoluteIndex;

              if (row?.slug || row?.subRows?.length) {
                return <MDTypography sx={{ textAlign: "left", pl: 1 }}>-</MDTypography>;
              }

              // Critical subrows => always displayed on screen in red (overtime rows)
              if (
                payrollSubmitted ||
                isCriticalSubRow ||
                (correctionActive && !row?.rosterData?.isCorrection)
              ) {
                return <span>{`$${row?.rosterData?.billRate || "0"}`}</span>;
              }

              return (
                <EditableCellNumber
                  amount={row?.rosterData?.billRate ?? ""}
                  setAmount={(val) => onChangeField(row, "billRate", val ? parseFloat(val) : val)}
                  // onValidate={(val) =>
                  //   validatePayBillRate(val, row?.rosterData?.payRate ?? "", "billRate")
                  // }
                  adornmentSymbol="$"
                  onKeyDown={(e) => handleKeyDownField(e, finalIndex, 1)}
                  ref={(el) => {
                    if (el) {
                      inputRefs.current[getFieldKey(finalIndex, 1)] = el;
                    }
                  }}
                />
              );
            },
          },
          {
            title: "Earning ID",
            field: "rosterData",
            width: 180,
            sortable: false,
            customCell: (rosterData, field, row, i, iSub, isCriticalSubRow, absoluteIndex) => {
              const finalIndex = absoluteIndex;

              // Critical subrows => always displayed on screen in red (overtime rows)
              if (
                payrollSubmitted ||
                isCriticalSubRow ||
                (correctionActive && !row?.rosterData?.isCorrection)
              ) {
                return <span>{`${row?.rosterData?.earningId || ""}`}</span>;
              }

              return row?.subRows?.length ? (
                <MDTypography sx={{ textAlign: "left", pl: 3 }}>-</MDTypography>
              ) : (
                <EditableCellDropdown
                  value={row?.rosterData?.earningId ?? ""}
                  setValue={(val) => onChangeField(row, "earningId", val)}
                  // onKeyDown={(e) => handleKeyDownField(e, finalIndex, 2)}
                  // ref={(el) => {
                  //   if (el) {
                  //     inputRefs.current[getFieldKey(finalIndex, 2)] = el;
                  //   }
                  // }}
                />
              );
            },
          },
          {
            title: "Total Hrs",
            field: "rosterData",
            width: 90,
            sortable: false,
            customCell: (rosterData, field, row, i, iSub) => {
              let hourDifference = 0;
              let hours = calculateTotalHours(row, currentEvent?.timeZone);
              if (correctionActive && row?.rosterData?.isCorrection) {
                // This row is corrected. Display difference of hours
                const originalHours = calculateTotalHours(
                  {
                    ...row,
                    rosterData: {
                      ...row?.rosterData,
                      timeIn: row?.rosterData?.originalTimeIn,
                      timeOut: row?.rosterData?.originalTimeOut,
                    },
                  },
                  currentEvent?.timeZone
                );
                hourDifference = hours - originalHours;
                hourDifference = Math.round(hourDifference * 100) / 100;
              }
              if (row?.subRows?.length) {
                hours = row.subRows.reduce((prev, current) => {
                  return (
                    prev +
                    calculateTotalHours(
                      {
                        ...current,
                        rosterData: {
                          ...current?.rosterData,
                          timeIn:
                            current?.rosterData?.originalTimeIn ?? current?.rosterData?.timeIn,
                          timeOut:
                            current?.rosterData?.originalTimeOut ?? current?.rosterData?.timeOut,
                        },
                      },
                      currentEvent?.timeZone
                    )
                  );
                }, 0);
              }
              if (row?.members?.length) {
                hours = row.members.reduce((prev, current) => {
                  return (
                    prev +
                    calculateTotalHours(
                      {
                        ...current,
                        rosterData: {
                          ...current?.rosterData,
                          timeIn:
                            current?.rosterData?.originalTimeIn ?? current?.rosterData?.timeIn,
                          timeOut:
                            current?.rosterData?.originalTimeOut ?? current?.rosterData?.timeOut,
                        },
                      },
                      currentEvent?.timeZone
                    )
                  );
                }, 0);
              }

              const hoursRounded = Math.round(hours * 100) / 100;
              const hourDifferenceSign = hourDifference >= 0 ? "+" : "-";

              return `${hoursRounded}${
                correctionActive && row?.rosterData?.isCorrection
                  ? ` (${hourDifferenceSign}${Math.abs(hourDifference)})`
                  : ""
              }`;
            },
          },
          {
            title: "Deduc.",
            field: "rosterData",
            width: 80,
            sortable: false,
            customCell: (rosterData, field, row) => {
              return row?.slug || row?.subRows?.length ? (
                <MDTypography sx={{ textAlign: "left", pl: 1 }}>-</MDTypography>
              ) : (
                <Typography
                  variant="text"
                  sx={{
                    color: `${
                      // eslint-disable-next-line no-nested-ternary
                      parseFloat(rosterData?.deductionAmount ?? 0) > 0
                        ? "success"
                        : parseFloat(rosterData?.deductionAmount ?? 0) < 0
                        ? "error"
                        : "dark"
                    }.main`,
                  }}
                >
                  {`(${(rosterData?.deductionAmount || 0) < 0 ? "-" : ""}${
                    rosterData?.deductionAmountType === "flat" ? "$" : ""
                  }${Math.abs(rosterData?.deductionAmount || 0)}${
                    rosterData?.deductionAmountType === "percentage" ? "%" : ""
                  })`}
                </Typography>
              );
            },
          },
        ]
      : []),
    ...(fullscreen || !showRosterData
      ? [
          {
            title: "Total Pay",
            field: "rosterData",
            width: 130,
            sortable: false,
            customCell: (rosterData, field, row, i, iSub) => {
              if (!row?.uniqueKey || extraColumns[row?.uniqueKey]?.totalPay == null) {
                return (
                  <Tooltip title="Total Pay not available">
                    <PriorityHigh color="error" />
                  </Tooltip>
                );
              }

              const result = extraColumns[row?.uniqueKey].totalPay.toFixed(2);

              return <span>{`$${result}`}</span>;
            },
          },
          {
            title: "BDN",
            field: "rosterData",
            width: 130,
            sortable: false,
            customCell: (rosterData, field, row, i, iSub) => {
              if (isLoadingBurdenRate) {
                return <CircularProgress size="16px" color="secondary" />;
              }

              if (
                !burdenRate?.data?.burdenInfo ||
                !row?.uniqueKey ||
                extraColumns[row?.uniqueKey]?.burden == null
              ) {
                return (
                  <Tooltip title="Burden Rate not available">
                    <PriorityHigh color="error" />
                  </Tooltip>
                );
              }

              const br = Math.round(calculatedBurdenRate * 100) / 100;
              const result = extraColumns[row?.uniqueKey].burden.toFixed(2);

              return (
                <Tooltip title={`Estimated Burden Rate: ${br * 100}%`}>
                  <span>{`~$${result}`}</span>
                </Tooltip>
              );
            },
          },
          {
            title: "Total Bill",
            field: "rosterData",
            width: 130,
            sortable: false,
            customCell: (rosterData, field, row, i, iSub) => {
              if (!row?.uniqueKey || extraColumns[row?.uniqueKey]?.totalBill == null) {
                return (
                  <Tooltip title="Total Bill not available">
                    <PriorityHigh color="error" />
                  </Tooltip>
                );
              }

              const result = extraColumns[row?.uniqueKey].totalBill.toFixed(2);

              return <span>{`$${result}`}</span>;
            },
          },
          {
            title: "GM",
            field: "rosterData",
            width: 130,
            sortable: false,
            customCell: (rosterData, field, row, i, iSub) => {
              if (isLoadingBurdenRate) {
                return <CircularProgress size="16px" color="secondary" />;
              }

              if (
                !burdenRate?.data?.burdenInfo ||
                !row?.uniqueKey ||
                extraColumns[row?.uniqueKey]?.grossMargin == null
              ) {
                return (
                  <Tooltip title="Burden Rate not available">
                    <PriorityHigh color="error" />
                  </Tooltip>
                );
              }

              const result = extraColumns[row?.uniqueKey].grossMargin.toFixed(2);
              const result2 = (
                (extraColumns[row?.uniqueKey].grossMarginPercentage ?? 0) * 100
              ).toFixed(0);

              const br = Math.round(calculatedBurdenRate * 100) / 100;

              return (
                <Tooltip title={`Estimated Burden Rate: ${br * 100}%\nGM%: ${result2}%`}>
                  <span>{`~$${result}`}</span>
                </Tooltip>
              );
            },
          },
        ]
      : []),
    {
      title: "Actions",
      field: "_id",
      width: 200,
      sortable: false,
      customCell: (rowId, field, row, ind, iSub, isCriticalSubRow) => (
        <PayrollEditorTableActions
          id={rowId}
          row={row}
          setNavigateUrl={navigateWithBack}
          isShiftJob={isShiftJob}
          isExpanded={expandedIndexes[ind]}
          onToggleSubList={() => handleToggleSubList(ind)}
          onOpenPartnerMembers={handleSelectPartnerMembers}
          deductionsOnly={iSub != null}
          onSelectRowForDeductions={setSelectedRowForDeductions}
          onSelectRowForTaxDetails={setSelectedRowForTaxDetails}
          editLocked={payrollSubmitted || isCriticalSubRow}
          onSelectRowForCorrection={setSelectedRowForCorrection}
          correctionActive={correctionActive}
        />
      ),
    },
  ];

  return (
    <>
      <Card
        className={classes.box}
        sx={
          fullscreen
            ? undefined
            : {
                height: 850,
              }
        }
      >
        <Grid container justifyContent="space-between" alignItems="flex-start" flexWrap="nowrap">
          <MDBox>
            <Grid container spacing={3} alignItems="center" justifyContent="space-between" py={1}>
              <Grid container item justifyContent="space-between">
                <MDBox display="flex">
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width="4rem"
                    height="4rem"
                    variant="gradient"
                    bgColor="success"
                    color="white"
                    borderRadius="xl"
                    ml={3}
                    mt={-4}
                  >
                    <Icon fontSize="large">request_quote</Icon>
                  </MDBox>
                  <MDTypography variant="h5" color="dark" sx={{ ml: 2 }}>
                    {title}
                  </MDTypography>
                </MDBox>
              </Grid>
            </Grid>

            <MDBox sx={{ ml: 3, mb: 2, maxWidth: fullscreen ? "unset" : "600px" }}>
              {payrollSubmitted && !correctionActive && (
                <Grid container item alignItems="center" flexWrap="nowrap">
                  <PriorityHigh
                    color="white"
                    fontSize="medium"
                    sx={{
                      mr: 1,
                      padding: 0.3,
                      backgroundColor: "error.main",
                      borderRadius: "50%",
                    }}
                  />
                  <MDTypography variant="body" color="error">
                    The Payroll has already been submitted. Create a correction in the Timecard
                    editor to update it.
                  </MDTypography>
                </Grid>
              )}
              {correctionActive && (
                <Grid container item alignItems="center" flexWrap="nowrap">
                  <Edit
                    color="white"
                    fontSize="medium"
                    sx={{
                      mr: 1,
                      padding: 0.3,
                      backgroundColor: "warning.main",
                      borderRadius: "50%",
                    }}
                  />
                  <MDTypography variant="body" color="warning">
                    Correction in progress
                  </MDTypography>
                </Grid>
              )}
            </MDBox>

            {!isShiftJob && (
              <MDBox ml={3}>
                <MDBox display="flex" flexWrap="wrap" alignItems="center">
                  <TimecardEditorFilterCards
                    setPage={setPage}
                    parent="Venues"
                    setFilters={setApplicantsFilters}
                    filters={{ ...applicantsFilters }}
                    doReplace
                    eventUrl={currentEvent?.eventUrl}
                  />
                </MDBox>
              </MDBox>
            )}
            <MDBox ml={3} sx={{ width: "50%" }}>
              <CustomTextField
                type="text"
                label="Department Name"
                showError
                InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: 20 }}
                Input
                onChange={(e) => setDepartmentName(e.target.value)}
                onBlur={handleUpdateDepartmentName}
                value={departmentName}
                placeholder={(currentEvent?.eventName || currentJob?.title || "").slice(0, 20)}
                disabled={
                  isLoading ||
                  timecardNotSubmitted ||
                  submitDataMutation.isLoading ||
                  isLoadingPayrollStatus ||
                  payrollSubmitted ||
                  wrongTimePeriod
                }
              />
            </MDBox>
          </MDBox>
          <MDBox sx={{ paddingTop: 1, paddingRight: 2 }}>
            <MDBox sx={{ display: "flex", alignItems: "center" }}>
              {saveRowMutation.isLoading && (
                <>
                  <MDTypography variant="text" color="dark" sx={{ mr: 2 }}>
                    Saving
                  </MDTypography>
                  <CircularProgress color="secondary" size={20} sx={{ marginRight: 3 }} />
                </>
              )}
              {fullscreen && (
                <MDBox display="flex" justifyContent="center" alignItems="center" mr={2}>
                  <SplitPanelSelector
                    panelOptions={localPanelOptions}
                    selectedPanel="payroll"
                    isEdit
                  />
                </MDBox>
              )}
              {!fullscreen && (
                <Tooltip title={showRosterData ? "Hide Fields" : "Display Fields"}>
                  <span>
                    <IconButton
                      color={showRosterData ? "success" : "secondary"}
                      onClick={handleToggleMinimizedInformation}
                    >
                      <Edit fontSize="large" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Totals">
                <span>
                  <IconButton
                    color="success"
                    onClick={() => setTaxTotalsOpen(true)}
                    disabled={
                      isLoading ||
                      timecardNotSubmitted ||
                      isLoadingBurdenRate ||
                      !calculatedBurdenRate
                    }
                  >
                    <Calculate fontSize="large" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={correctionActive ? "Cancel Correction" : "Reset"}>
                <span>
                  <IconButton
                    color="error"
                    onClick={() => setResetConfirmationModalOpen(true)}
                    disabled={
                      isLoading || timecardNotSubmitted || payrollSubmitted || wrongTimePeriod
                    }
                  >
                    {correctionActive ? <Delete fontSize="large" /> : <Restore fontSize="large" />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Undo">
                <span>
                  <IconButton
                    color="info"
                    onClick={handleUndo}
                    disabled={
                      isLoading || timecardNotSubmitted || payrollSubmitted || wrongTimePeriod
                    }
                  >
                    <Undo fontSize="large" />
                  </IconButton>
                </span>
              </Tooltip>
              {!fullscreen && (
                <Tooltip title="Go Fullscreen">
                  <span>
                    <IconButton color="info" onClick={toggleFullscreen}>
                      <AspectRatio fontSize="large" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {fullscreen && (
                <Tooltip title="Minimize">
                  <span>
                    <IconButton color="info" onClick={minimize}>
                      <CloseFullscreen fontSize="large" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </MDBox>
            <MDBox
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                mt: 2,
                gap: 2,
              }}
            >
              <MDButton
                onClick={handleExport}
                color="success"
                variant="contained"
                disabled={
                  isLoading ||
                  submitDataMutation.isLoading ||
                  isLoadingPayrollStatus ||
                  saveRowMutation.isLoading ||
                  wrongTimePeriod
                }
              >
                Export
              </MDButton>
              <MDButton
                onClick={() => setSubmitConfirmationModalOpen(true)}
                color="info"
                variant="contained"
                disabled={
                  isLoading ||
                  timecardNotSubmitted ||
                  submitDataMutation.isLoading ||
                  isLoadingPayrollStatus ||
                  payrollSubmitted ||
                  saveRowMutation.isLoading ||
                  wrongTimePeriod
                }
              >
                Submit
              </MDButton>
            </MDBox>
          </MDBox>
        </Grid>
        <MDBox pt={2} pb={2} pl={!fullscreen ? 2 : 0} pr={!fullscreen ? 2 : 0}>
          {wrongTimePeriod && (
            <MDBox sx={{ mt: 1, mb: 4 }}>
              <MDTypography variant="h4" color="dark" sx={{ ml: 2, textAlign: "center" }}>
                Wrong Period
              </MDTypography>
              <MDTypography variant="h5" color="secondary" sx={{ ml: 2, textAlign: "center" }}>
                {`This event ${
                  eventIsInThePast ? "took" : "takes"
                } place in a different time period.`}
              </MDTypography>
            </MDBox>
          )}
          {!wrongTimePeriod && timecardNotSubmitted && (
            <MDBox sx={{ mt: 1, mb: 4 }}>
              <MDTypography variant="h4" color="dark" sx={{ ml: 2, textAlign: "center" }}>
                Timecard not submitted
              </MDTypography>
              <MDTypography variant="h5" color="secondary" sx={{ ml: 2, textAlign: "center" }}>
                {`Please submit the ${
                  isShiftJob ? "Job" : "Event"
                } in the Timecard Editor first and then try again.`}
              </MDTypography>
            </MDBox>
          )}
          {!wrongTimePeriod && !timecardNotSubmitted && (
            <DataTableWithSubrows
              columns={columns}
              data={rosterList?.data ?? []}
              page={page}
              limit={fullscreen ? limit : defaultMinimizedPageSize}
              setPage={setPage}
              setLimit={setLimit}
              isLoading={isLoading}
              // onRowClick={isNotClientOrEventAdmin ? (row, e) => handleRowClick(row, e) : () => {}}
              order={order}
              orderBy={orderBy}
              toggleSort={toggleSort}
              fetchAll={false}
              defaultSort="lastName:asc"
              idField="_id"
              {...(fullscreen ? {} : { height: 565 })}
              hidePageSizeSelector={!fullscreen}
              stickyHeader={!fullscreen}
              openIndexes={expandedIndexes}
            />
          )}
        </MDBox>
      </Card>
      {isPartnerMembersModalOpen && (
        <TimecardEditorPartnerMembersModal
          isOpen={isPartnerMembersModalOpen}
          toggleModal={handleClosePartnerMembersModal}
          partner={selectedPartner}
          eventDay={eventDay}
          saveRowMutation={saveRowMutation}
          navigateWithBack={navigateWithBack}
          currentPeriod={currentPeriod}
          isForPayrollEditor
          editLocked={payrollSubmitted}
          handleConfirmCreateCorrection={handleConfirmCreateCorrection}
          correctionActive={correctionActive}
        />
      )}
      {!!selectedRowForTaxDetails && (
        <TaxDetailsModal
          open={!!selectedRowForTaxDetails}
          setOpen={() => {
            setSelectedRowForTaxDetails(null);
          }}
          row={selectedRowForTaxDetails}
          title={isShiftJob ? currentJob?.title : currentEvent?.eventName}
          date={isShiftJob ? null : currentEvent?.eventDate}
          eventUrl={isShiftJob ? null : currentEvent?.eventUrl}
          jobSlug={isShiftJob ? currentJob?.jobSlug : null}
          startDate={currentPeriod[0].toISOString()}
          endDate={currentPeriod[1].toISOString()}
        />
      )}
      {!!taxTotalsOpen && (
        <TaxTotalsModal
          open={taxTotalsOpen}
          setOpen={() => {
            setTaxTotalsOpen(null);
          }}
          title={isShiftJob ? currentJob?.title : currentEvent?.eventName}
          date={isShiftJob ? null : currentEvent?.eventDate}
          eventUrl={isShiftJob ? null : currentEvent?.eventUrl}
          jobSlug={isShiftJob ? currentJob?.jobSlug : null}
          startDate={currentPeriod[0].toISOString()}
          endDate={currentPeriod[1].toISOString()}
          approximatedBurdenRate={calculatedBurdenRate}
        />
      )}
      {!!selectedRowForDeductions && (
        <AddDeductionsModal
          open={!!selectedRowForDeductions}
          setOpen={() => {
            setSelectedRowForDeductions(null);
          }}
          onAddDeduction={handleAddDeduction}
          row={selectedRowForDeductions}
        />
      )}
      {isResetConfirmationModalOpen && (
        <ConfirmationModal
          isOpen={isResetConfirmationModalOpen}
          onClose={() => {
            setResetConfirmationModalOpen(false);
          }}
          title={correctionActive ? "Cancel Correction" : "Reset data"}
          body={
            correctionActive
              ? "Are you sure you want to cancel the correction? This action cannot be undone."
              : "Are you sure you want to reset the data? This will undo all changes made in this editor and restore the values of the submitted timecard. This action cannot be undone."
          }
          onConfirm={(e) => {
            e.stopPropagation();
            handleReset();
            setResetConfirmationModalOpen(false);
          }}
        />
      )}
      {isSubmitConfirmationModalOpen && (
        <ConfirmationModal
          isOpen={isSubmitConfirmationModalOpen}
          onClose={() => {
            setSubmitConfirmationModalOpen(false);
          }}
          title="Submit Timecard"
          body="Are you sure you want to submit payroll? This will lock the payroll editor."
          onConfirm={(e) => {
            e.stopPropagation();
            handleSubmit();
            setSubmitConfirmationModalOpen(false);
          }}
        />
      )}
      {isExportConfirmationModalOpen && (
        <ConfirmationModal
          isOpen={isExportConfirmationModalOpen}
          onClose={() => {
            setExportConfirmationModalOpen(false);
          }}
          title="Export Payroll"
          body="Payroll has been submitted successfully. Do you want to export the timesheet now?"
          onConfirm={(e) => {
            e.stopPropagation();
            handleExport();
            setExportConfirmationModalOpen(false);
          }}
        />
      )}
      {!!selectedRowForCorrection && (
        <ConfirmationModal
          isOpen={!!selectedRowForCorrection}
          onClose={() => {
            setSelectedRowForCorrection(null);
          }}
          title="Create correction"
          body="Are you sure you want to create a correction for this row?"
          onConfirm={(e) => {
            e.stopPropagation();
            handleConfirmCreateCorrection(selectedRowForCorrection);
            setSelectedRowForCorrection(null);
          }}
        />
      )}
    </>
  );
};

// Setting default values for the props
PayrollEditorTable.defaultProps = {
  // currentVenue: {},
  title: "Applicants",
};

// Typechecking props
PayrollEditorTable.propTypes = {
  // currentVenue: PropTypes.object,
  title: PropTypes.string,
};

export default PayrollEditorTable;
